import { useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMigration } from '@/lib/migration/useMigration'
import { TrackerShell } from '@/components/migration/MigrationShell'
import { MigrationItemDrawer } from '@/components/migration/MigrationItemDrawer'
import { ItemModal } from '@/components/migration/ItemModal'
import { Empty, StatusChip } from '@/components/migration/primitives'
import { isBlocked } from '@/lib/migration/engine'
import { STATUS_TOKEN, unassignedFindings } from '@/lib/migration/summary'
import type { DiscoveryFinding, MigrationItem } from '@/api/types/migration'
import { Chip, Icon } from '@/components/readiness/primitives'
import { FilterSelect } from '@/components/readiness/FilterSelect'
import { formatDate } from '@/components/common/format'
import { LockedPreview } from '@/components/readiness/LockedPreview'
import { useIsLocked } from '@/stores/profileStore'

// Roadmap (I3 §9): one column per wave in the plan plus Unscheduled
// (Discovery findings with no item). Read-only on the wave plan: nothing
// here writes to it, and no service serves one today, so the columns are
// usually empty. Cards open the §8 drawer.
// POC (I6 POC §5): part of the locked Migration Tracker, gated from the filter row.

type Group = 'wave' | 'quarter'

export default function Roadmap() {
  const ctx = useMigration()
  const { index, mscope } = ctx
  const [params, setParams] = useSearchParams()
  const [group, setGroup] = useState<Group>('wave')
  const [system, setSystem] = useState('')
  const [showClosed, setShowClosed] = useState<Record<string, boolean>>({})
  const [newFrom, setNewFrom] = useState<DiscoveryFinding | null>(null)
  const locked = useIsLocked('migration_tracker')
  const gateRef = useRef<HTMLDivElement>(null)
  const selectedId = locked ? null : params.get('item')
  const select = (id: string | null) => {
    const p = new URLSearchParams(params)
    if (id) p.set('item', id)
    else p.delete('item')
    setParams(p, { replace: true })
  }
  const facts = mscope?.facts
  const milestone = index.submission ? { date: index.submission.deadline, label: index.submission.label } : null

  const columns = useMemo(() => {
    if (!mscope) return []
    const items = mscope.items.filter((i) => !system || i.system_id === system)
    if (group === 'wave') {
      return (facts?.waves ?? []).sort((a, b) => a.order - b.order).map((w) => ({ key: w.id, label: w.label, range: `${formatDate(w.from)} – ${formatDate(w.to)}`, descriptor: w.rationale, items: items.filter((i) => i.wave_id === w.id), defaultClosed: w.order === 1, from: w.from, to: w.to }))
    }
    const byQ = new Map<string, MigrationItem[]>()
    items.forEach((i) => {
      if (!i.target_date) return
      const d = new Date(i.target_date)
      const k = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`
      byQ.set(k, [...(byQ.get(k) ?? []), i])
    })
    return Array.from(byQ.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([k, its]) => ({ key: k, label: k.replace('-', ' '), range: '', descriptor: 'By target date', items: its, defaultClosed: false, from: '', to: '' }))
  }, [mscope, facts, group, system])

  const unscheduled = mscope ? unassignedFindings(mscope).filter((f) => !system || f.system_id === system) : []
  const unwaved = mscope ? mscope.items.filter((i) => !i.wave_id && i.status !== 'closed' && (!system || i.system_id === system)) : []
  const visibleIds = columns.flatMap((c) => c.items.filter((i) => i.status !== 'closed' || (showClosed[c.key] ?? c.defaultClosed)).map((i) => i.id)).concat(unwaved.map((i) => i.id))
  const selected = selectedId ? mscope?.items.find((i) => i.id === selectedId) ?? null : null
  const msBetween = (() => {
    if (!milestone || group !== 'wave') return null
    const t = new Date(milestone.date).getTime()
    for (let k = 0; k < columns.length - 1; k++) {
      if (new Date(columns[k].to).getTime() < t && t < new Date(columns[k + 1].from).getTime()) return `${milestone.label} (${formatDate(milestone.date)}) falls between ${columns[k].label} and ${columns[k + 1].label}.`
    }
    const hit = columns.find((c) => c.from && c.to && new Date(c.from).getTime() <= t && t <= new Date(c.to).getTime())
    return hit ? `${milestone.label} (${formatDate(milestone.date)}) falls within ${hit.label}.` : null
  })()

  return (
    <TrackerShell ctx={ctx} testId="migration-roadmap">
      {mscope && (
        <LockedPreview feature="migration_tracker" gateAt={gateRef} title="Migration Tracker" body="Plan and track every migration against the CBOM, with vendor-gated gaps, owners and target dates rolled up into the readiness score.">
          <div ref={gateRef} className="rd-toolbar mb-3 text-[12.5px]">
            <span className="rd-segmented !h-[42px]" role="radiogroup" aria-label="Group">
              {(['wave', 'quarter'] as Group[]).map((g) => (
                <button key={g} type="button" role="radio" aria-checked={group === g} onClick={() => setGroup(g)}>Group · {g === 'wave' ? 'Wave' : 'Quarter'}</button>
              ))}
            </span>
            <FilterSelect label="System" value={system} onChange={setSystem} any options={(facts?.systems ?? []).map((s) => [s.id, s.name])} />
            <Chip tone="tile">Wave plan{facts?.wave_plan_updated_at ? ` · updated ${formatDate(facts.wave_plan_updated_at)}` : ''}</Chip>
            {msBetween && <span style={{ color: 'var(--rd-status-warn)' }} data-testid="milestone-note">{msBetween}</span>}
            <span className="ml-auto" style={{ color: 'var(--rd-text-faint)' }}>Read-only: nothing here writes to the wave plan; an item's wave is changed on the item.</span>
          </div>

          {columns.length === 0 && unscheduled.length === 0 ? (
            <Empty>No wave plan and no unassigned findings.</Empty>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="roadmap-columns">
              {columns.map((c) => {
                const show = showClosed[c.key] ?? c.defaultClosed
                const visible = c.items.filter((i) => i.status !== 'closed' || show)
                const blockedCount = c.items.filter((i) => i.status !== 'closed' && isBlocked(i, mscope.items, mscope.item_deps)).length
                return (
                  <section key={c.key} className="rd-card w-[280px] shrink-0 p-3" style={blockedCount ? { border: '1px solid var(--rd-status-warn)' } : undefined} aria-label={c.label} data-testid={`wave-col-${c.key}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[14px] font-semibold">{c.label} <span className="rd-mono text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{c.items.length}</span></p>
                        <p className="text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>{c.range}</p>
                      </div>
                      <label className="flex items-center gap-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>
                        <input type="checkbox" checked={show} onChange={(e) => setShowClosed({ ...showClosed, [c.key]: e.target.checked })} /> closed
                      </label>
                    </div>
                    <p className="mt-1 text-[11.5px] italic" style={{ color: 'var(--rd-text-faint)' }}>{c.descriptor}</p>
                    <ul className="mt-3 space-y-2">
                      {visible.map((it) => <Card key={it.id} ctx={ctx} item={it} onOpen={() => select(it.id)} />)}
                      {visible.length === 0 && <li className="text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>Nothing scheduled.</li>}
                    </ul>
                  </section>
                )
              })}
              {unwaved.length > 0 && group === 'wave' && (
                <section className="rd-card w-[280px] shrink-0 p-3" aria-label="No wave" style={{ border: '1px dashed var(--rd-border-control)' }}>
                  <p className="text-[14px] font-semibold">No wave <span className="rd-mono text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{unwaved.length}</span></p>
                  <p className="text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>Items without a wave · set it on the item</p>
                  <ul className="mt-3 space-y-2">{unwaved.map((it) => <Card key={it.id} ctx={ctx} item={it} onOpen={() => select(it.id)} />)}</ul>
                </section>
              )}
              <section className="w-[280px] shrink-0 rounded-[12px] p-3" style={{ border: '1px dashed var(--rd-status-warn)', background: 'var(--rd-tint-l1)' }} aria-label="Unscheduled" data-testid="unscheduled-col">
                <p className="text-[14px] font-semibold">Unscheduled <span className="rd-mono text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{unscheduled.length}</span></p>
                <p className="text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>Discovery findings with no migration item</p>
                <ul className="mt-3 space-y-2">
                  {unscheduled.map((f) => (
                    <li key={f.id} className="rounded-[8px] p-2.5 text-[12.5px]" style={{ background: 'var(--rd-bg-card)', borderLeft: '3px solid var(--rd-status-warn)' }}>
                      <p className="font-medium">{f.title}</p>
                      <p className="rd-mono text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>{f.algorithm} · {facts?.systems.find((s) => s.id === f.system_id)?.name}</p>
                      <button type="button" className="rd-link mt-1" onClick={() => setNewFrom(f)} data-testid={`create-from-${f.id}`}>Create migration item →</button>
                    </li>
                  ))}
                  {unscheduled.length === 0 && <li className="text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>Every finding has an item.</li>}
                </ul>
              </section>
            </div>
          )}
        </LockedPreview>
      )}
      {selected && <MigrationItemDrawer ctx={ctx} item={selected} prev={visibleIds[visibleIds.indexOf(selected.id) - 1] ?? null} next={visibleIds[visibleIds.indexOf(selected.id) + 1] ?? null} onNavigate={select} onClose={() => select(null)} />}
      {newFrom && !locked && <ItemModal ctx={ctx} finding={newFrom} onClose={() => setNewFrom(null)} onCreated={(it) => { setNewFrom(null); select(it.id) }} />}
    </TrackerShell>
  )
}

function Card({ ctx, item, onOpen }: { ctx: ReturnType<typeof useMigration>; item: MigrationItem; onOpen: () => void }) {
  const { mscope } = ctx
  if (!mscope) return null
  const dep = mscope.item_deps.find((d) => d.item_id === item.id)
  const depItem = dep ? mscope.items.find((i) => i.id === dep.depends_on_item_id) : null
  const blocked = !!depItem && depItem.status !== 'closed' && item.status !== 'closed'
  const sys = mscope.facts?.systems.find((s) => s.id === item.system_id)
  return (
    <li>
      <button type="button" onClick={onOpen} className="w-full rounded-[8px] p-2.5 text-left text-[12.5px]" style={{ background: 'var(--rd-bg-tile)', borderLeft: `3px solid ${STATUS_TOKEN[item.status]}` }} data-testid={`card-${item.code}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 flex-1 font-medium leading-snug">{item.title}</p>
          <StatusChip status={item.status} />
        </div>
        <p className="rd-mono mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>{item.current_algorithm || '—'} → {item.target_algorithm || 'TBD'} · {sys?.name ?? item.target_date ?? '—'}</p>
        {depItem && (
          <p className="mt-1 flex items-center gap-1 text-[11.5px]" style={{ color: blocked ? 'var(--rd-status-warn)' : 'var(--rd-text-faint)' }}>
            <Icon name="arrow-right" size={11} /> {blocked ? `blocked: ${depItem.code}` : `after ${depItem.code}`}
          </p>
        )}
      </button>
    </li>
  )
}
